import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';

interface HeaderProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}

export function Header({ sidebarOpen, onToggleSidebar }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setMenuOpen(false); triggerRef.current?.focus(); }
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-30 flex h-14 items-center justify-between px-4">
      {/* Glass background */}
      <div className="absolute inset-0 glass-subtle" />
      {/* Gradient accent line at bottom */}
      <div className="gradient-line absolute bottom-0 left-0 right-0 h-px" />

      {/* Left: Sidebar toggle + Logo */}
      <div className="relative flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-text-secondary transition-all hover:bg-primary/10 hover:text-primary"
          aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
        </button>

        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2.5 rounded-lg px-1 py-0.5 transition-all hover:bg-primary/10"
          title="New Chat"
        >
          <img
            src="/logo.png"
            alt="Logo"
            className="h-7 w-7 rounded-md object-contain"
          />
          <span className="font-heading text-lg font-bold tracking-tight">
            <span className="bg-gradient-to-r from-primary to-sky-300 bg-clip-text text-transparent">AI</span>{' '}
            <span className="text-text-primary">Hub</span>
          </span>
        </button>
      </div>

      {/* Right: User dropdown */}
      {user && (
        <div className="relative flex items-center gap-2">
          {/* User menu trigger */}
          <div ref={menuRef} className="relative">
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-lg px-1.5 py-1 transition-all hover:bg-bg-tertiary/60"
              aria-haspopup="true"
              aria-expanded={menuOpen}
            >
              <span className="hidden font-body text-sm font-medium text-text-primary sm:inline">
                {user.displayName}
              </span>
              {/* Chevron */}
              <svg className={`hidden h-4 w-4 text-text-muted transition-transform sm:block ${menuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-48 overflow-hidden rounded-xl border border-border bg-bg shadow-lg" onKeyDown={handleMenuKeyDown}>
                <div className="px-3 py-2 border-b border-border-light">
                  <p className="truncate font-body text-sm font-medium text-text-primary">{user.displayName}</p>
                  <p className="truncate font-body text-xs text-text-muted">{user.email}</p>
                  {user.jobTitle && (
                    <p className="truncate font-body text-xs text-text-muted">{user.jobTitle}</p>
                  )}
                  {user.department && (
                    <p className="truncate font-body text-xs text-text-muted">{user.department}</p>
                  )}
                </div>
                <div className="p-1">
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); navigate('/settings'); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-text-secondary transition-all hover:bg-primary/10 hover:text-primary"
                  >
                    <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings
                  </button>
                  <div className="my-1 h-px bg-border-light" />
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); logout(); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-text-secondary transition-all hover:bg-error/10 hover:text-error"
                  >
                    <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
